import { LoadingButton, type LoadingButtonProps } from '@mui/lab'
import {
  type ButtonProps,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  type DialogTitleProps
} from '@mui/material'
import React from 'react'
import { useBoolean } from 'react-use'

type SetModalState = (nextState?: boolean) => void

export type ButtonWithConfirmationOnConfirm = (options: {
  setIsModalOpen: SetModalState
}) => unknown

type BaseButtonProps = {
  buttonCancelText?: string
  buttonConfirmText: string
  dialogBodyContent: string | React.ReactElement
  dialogTitleColor?: DialogTitleProps['color']
  dialogTitleContent: string | React.ReactElement
  onConfirm: ButtonWithConfirmationOnConfirm
  renderButton?: (props: {
    setIsModalOpen: SetModalState
  }) => React.ReactElement
}

type ButtonWithConfirmationProps = BaseButtonProps &
  Omit<ButtonProps, 'onClick'> & {
    buttonConfirmProps?: Omit<LoadingButtonProps, 'onClick'>
  }

export const ButtonWithConfirmation: React.FC<ButtonWithConfirmationProps> = ({
  buttonCancelText = 'Cancel',
  buttonConfirmProps,
  buttonConfirmText,
  children,
  dialogBodyContent,
  dialogTitleContent,
  dialogTitleColor,
  onConfirm,
  renderButton,
  ...rest
}) => {
  const [isModalOpen, setIsModalOpen] = useBoolean(false)

  const handleConfirmClick = async () => {
    await onConfirm({ setIsModalOpen })
  }

  return (
    <>
      {renderButton ? (
        renderButton({ setIsModalOpen })
      ) : (
        <Button
          variant="contained"
          {...rest}
          onClick={() => setIsModalOpen(true)}
        >
          {children}
        </Button>
      )}
      <Dialog
        aria-labelledby="alert-dialog-title"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle color={dialogTitleColor} id="alert-dialog-title">
          {dialogTitleContent}
        </DialogTitle>
        <DialogContent>
          {typeof dialogBodyContent === 'string' ? (
            <DialogContentText>{dialogBodyContent}</DialogContentText>
          ) : (
            dialogBodyContent
          )}
        </DialogContent>
        <DialogActions>
          <Button
            data-testid={`dialog-button-cancel-${buttonCancelText}`}
            onClick={() => setIsModalOpen(false)}
          >
            {buttonCancelText}
          </Button>
          <LoadingButton
            autoFocus
            data-testid={`dialog-button-confirm-${buttonConfirmText}`}
            variant="contained"
            onClick={handleConfirmClick}
            {...buttonConfirmProps}
          >
            {buttonConfirmText}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
